import React, { useState, useEffect, useRef } from 'react';
import { GetAll, apiURL } from '../../utils/apiCalls';

// Cache for sticky header ads
const stickyAdsCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Sticky Header Ad Component
 * Appears at the top of the page and becomes sticky when scrolling
 * Scrolls through multiple ads horizontally
 */
const StickyHeaderAd = ({ positionId }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch ads
  useEffect(() => {
    /**
     * Get session identifier for personalized ad selection
     * Since ads are displayed on public pages (before login), we always use session ID
     * 
     * This ensures:
     * - Different browser sessions see different ads
     * - Same browser session sees consistent ads (on page reload)
     * - Each user/browser gets personalized ad selection
     * 
     * Session ID persists across page reloads but is unique per browser
     */
    const getUserIdentifier = () => {
      // Always use session ID since ads are shown before login
      // Get existing session ID or generate a new one
      let sessionId = sessionStorage.getItem('sticky_ads_session_id');
      
      if (!sessionId) {
        // Generate a unique session ID for this browser session
        // This persists across page reloads but is unique per browser
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('sticky_ads_session_id', sessionId);
      }
      
      return sessionId;
    };

    /**
     * Simple seeded random number generator
     * Uses a seed to generate consistent "random" numbers for the same seed
     * Different seeds produce different sequences
     */
    const seededRandom = (seed) => {
      let value = seed;
      return () => {
        value = (value * 9301 + 49297) % 233280;
        return value / 233280;
      };
    };

    /**
     * Hash string to numeric seed
     */
    const hashString = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return Math.abs(hash);
    };

    /**
     * Seeded Fisher-Yates shuffle algorithm for user-specific ad ordering
     * Uses user identifier to generate different shuffles for different users
     */
    const shuffleArrayWithSeed = (array, seed) => {
      const shuffled = [...array]; // Create a copy to avoid mutating original
      const random = seededRandom(seed);
      
      for (let i = shuffled.length - 1; i > 0; i--) {
        // Use seeded random instead of Math.random()
        const j = Math.floor(random() * (i + 1));
        // Swap elements at positions i and j
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    const fetchAds = async () => {
      if (!positionId) {
        setLoading(false);
        return;
      }

      // Check cache first
      const cacheKey = `sticky_position_${positionId}`;
      const cached = stickyAdsCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        // Filter cached ads to only include those with descriptions
        const adsWithDescriptions = cached.data.filter(ad => ad.description && ad.description.trim().length > 0);
        if (adsWithDescriptions.length === 0) {
          setLoading(false);
          return;
        }
        // Apply user-specific shuffle to cached ads
        const userIdentifier = getUserIdentifier();
        const baseSeed = hashString(userIdentifier);
        const shuffledAds = shuffleArrayWithSeed(adsWithDescriptions, baseSeed);
        setAds(shuffledAds);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await GetAll(apiURL + '/api/v1/ads');
        
        if (response && response.data && response.data.status) {
          const allAds = response.data.data || [];
          const filteredAds = allAds.filter(ad => String(ad.position_id) === String(positionId));
          
          if (filteredAds.length === 0) {
            setLoading(false);
            stickyAdsCache.set(cacheKey, { data: [], timestamp: Date.now() });
            return;
          }
          
          // Fetch uploads for all ads in parallel
          const uploadPromises = filteredAds.map(async (ad) => {
            try {
              const assetsResponse = await GetAll(apiURL + `/api/v1/ads/${ad.id}/uploads`);
              const uploads = assetsResponse?.data?.data || [];
              const assets = Array.isArray(uploads) ? uploads : (uploads.toJSON ? uploads.toJSON() : []);
              
              // Filter to get valid assets (completed or uploading with URLs)
              const validAssets = assets.filter(asset => {
                if (!asset.filepaths3) return false;
                if (asset.hasOwnProperty('status')) {
                  return asset.status === 'completed' || asset.status === 'uploading';
                }
                return true;
              });
              
              return { ...ad, assets: validAssets };
            } catch (err) {
              console.error(`Error fetching uploads for ad ${ad.id}:`, err);
              return { ...ad, assets: [] };
            }
          });
          
          const results = await Promise.allSettled(uploadPromises);
          const adsWithAssets = results
            .filter(result => result.status === 'fulfilled')
            .map(result => result.value)
            .filter(ad => ad.description && ad.description.trim().length > 0);
          
          // Cache the results (without shuffle)
          stickyAdsCache.set(cacheKey, { data: adsWithAssets, timestamp: Date.now() });
          
          // Apply user-specific shuffle before setting ads
          // This ensures different users see ads in different order
          const userIdentifier = getUserIdentifier();
          const baseSeed = hashString(userIdentifier);
          const shuffledAds = shuffleArrayWithSeed(adsWithAssets, baseSeed);
          
          console.log(`StickyHeaderAd - Shuffled ads for ${userIdentifier}: ${shuffledAds.length} ads`);
          setAds(shuffledAds);
        }
      } catch (error) {
        console.error('Error fetching sticky header ads:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, [positionId]);

  // Continuous smooth scrolling animation (marquee effect)
  useEffect(() => {
    if (ads.length === 0 || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    let scrollPosition = 0;
    const scrollSpeed = 0.5; // pixels per frame (smooth scroll)
    let animationId;

    const animate = () => {
      const containerWidth = container.parentElement?.offsetWidth || window.innerWidth;
      const totalWidth = containerWidth * ads.length;
      
      scrollPosition += scrollSpeed;
      
      // Reset to beginning when we've scrolled through all ads (seamless loop)
      if (scrollPosition >= totalWidth) {
        scrollPosition = 0;
      }

      container.style.transform = `translateX(-${scrollPosition}px)`;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [ads]);

  if (!positionId || loading || ads.length === 0) {
    return null;
  }

  const renderAd = (ad, index) => {
    if (!ad || !ad.description || !ad.description.trim()) return null;

    const handleClick = () => {
      if (ad.link_url) {
        window.open(ad.link_url, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <div
        key={ad.id || index}
        style={{
          minWidth: '100%',
          width: '100%',
          height: '30px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: ad.link_url ? 'pointer' : 'default',
          overflow: 'hidden',
          padding: '0 4px',
        }}
        onClick={handleClick}
      >
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            fontSize: '16px',
            lineHeight: '1.2',
            color: '#333',
            fontWeight: 400,
          }}
        >
          {ad.description}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .header.scrolling-header {
          margin-top: 0 !important;
          padding-top: 0 !important;
        }
      `}</style>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: '#fff',
          boxShadow: isScrolled ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
          transition: 'all 0.3s ease',
          margin: 0,
          marginTop: 0,
          marginBottom: 0,
          padding: 0,
          display: 'block',
          lineHeight: 0,
          verticalAlign: 'top',
        }}
      >
      <div style={{ position: 'relative', width: '100%', height: '30px', overflow: 'hidden', margin: 0, padding: 0 }}>
        <div
          ref={scrollContainerRef}
          style={{
            display: 'flex',
            width: `${ads.length * 100}%`,
            height: '100%',
            willChange: 'transform',
          }}
        >
          {ads.map((ad, index) => renderAd(ad, index))}
          {/* Duplicate ads for seamless infinite scroll */}
          {ads.length > 1 && ads.map((ad, index) => renderAd(ad, index + ads.length))}
        </div>
      </div>
    </div>
    </>
  );
};

export default StickyHeaderAd;

